import type { RRuleOptions } from "@ilamy/calendar";
import { RRule } from "rrule";

/** DB row type (snake_case, ISO strings) */
export interface CalendarEventRow {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_time: string;
  end_time: string;
  is_all_day: boolean;
  color: string | null;
  recurrence_rule: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Event shape for passing to IlamyCalendar's events prop.
 * Matches IlamyCalendarPropEvent (which isn't exported from the lib).
 */
export interface CalendarPropEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color?: string;
  description?: string;
  location?: string;
  allDay?: boolean;
  rrule?: RRuleOptions;
  data?: Record<string, unknown>;
}

/** Predefined event color options */
export const EVENT_COLORS = [
  { label: "Blue", value: "#3b82f6", bg: "#dbeafe" },
  { label: "Red", value: "#ef4444", bg: "#fee2e2" },
  { label: "Green", value: "#22c55e", bg: "#dcfce7" },
  { label: "Purple", value: "#a855f7", bg: "#f3e8ff" },
  { label: "Orange", value: "#f97316", bg: "#ffedd5" },
  { label: "Pink", value: "#ec4899", bg: "#fce7f3" },
  { label: "Yellow", value: "#eab308", bg: "#fef9c3" },
] as const;

/** Recurrence presets for the form */
export const RECURRENCE_PRESETS = [
  { label: "None", value: "" },
  { label: "Daily", value: "FREQ=DAILY" },
  { label: "Weekdays", value: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" },
  { label: "Weekly", value: "FREQ=WEEKLY" },
  { label: "Monthly", value: "FREQ=MONTHLY" },
  { label: "Yearly", value: "FREQ=YEARLY" },
] as const;

/**
 * Parse an RRULE string into RRuleOptions for ilamy calendar.
 * Returns undefined if the string is empty/null.
 */
export function parseRRule(rruleString: string | null, dtstart: Date): RRuleOptions | undefined {
  if (!rruleString) return undefined;

  try {
    const rule = RRule.fromString(`DTSTART:${formatDtstart(dtstart)}\nRRULE:${rruleString}`);
    return {
      ...rule.origOptions,
      freq: rule.origOptions.freq!,
      dtstart,
    };
  } catch {
    return undefined;
  }
}

function formatDtstart(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/** Convert a DB row to a calendar prop event for ilamy */
export function convertToIlamyEvent(row: CalendarEventRow): CalendarPropEvent {
  const start = new Date(row.start_time);
  const end = new Date(row.end_time);

  return {
    id: row.id,
    title: row.title,
    start,
    end,
    color: row.color ?? undefined,
    description: row.description ?? undefined,
    location: row.location ?? undefined,
    allDay: row.is_all_day,
    rrule: parseRRule(row.recurrence_rule, start),
    data: { dbEvent: row },
  };
}
